import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Container, Text } from '../../components/index.js'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { DownArrow } from './icons/index.js'
import { useNavContext } from './NavProvider.js'
import { ResourceItem } from './ResourceItem.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

/**
 * 资源位侧边栏下拉
 * @param categoriesItem 分类项
 */
export const ResourceSidebarDropdown = React.memo(
  ({ resourceCategoriesMetadata, resourcesMetadata }: { resourceCategoriesMetadata: any; resourcesMetadata: any }) => {
    const { onSidebarNavClick } = useNavContext()
    const [expandedSubcategory, setExpandedSubcategory] = useState<{ index: number; open: boolean }[]>([])
    const [activeSubSubcategoryIndex, setActiveSubSubcategoryIndex] = useState(-1)
    const resourceDropdownRef = useRef<HTMLDivElement>(null)
    const { locale = 'us' } = useAiuiContext()

    const initExpandedSubcategory = useCallback(() => {
      const subcategories = resourceCategoriesMetadata?.subcategories
      if (!subcategories?.length) return

      const withSubSubCategoriesIndex = subcategories.findIndex((item: any) => !!item?.subSubCategories)
      const withoutSubSubCategoriesIndex = subcategories.findIndex((item: any) => !item?.subSubCategories)

      // 创建展开状态数组
      const expandedStates = subcategories.map((_: any, index: number) => ({
        index,
        open: withSubSubCategoriesIndex === index || withoutSubSubCategoriesIndex === index,
      }))

      setExpandedSubcategory(expandedStates)
    }, [resourceCategoriesMetadata])

    useEffect(() => {
      initExpandedSubcategory()
    }, [initExpandedSubcategory])

    // 通过categoriesMetadata的subSubCategories的label与resources的label匹配
    const matchResourcesMetadata = useMemo(() => {
      const subCategory =
        resourceCategoriesMetadata?.subcategories?.[expandedSubcategory?.find(item => item.open)?.index || 0]
      const currentResourcesMetadata =
        resourcesMetadata?.find((item: any) => item?.label?.toLowerCase() === subCategory?.label?.toLowerCase()) || {}

      if (subCategory?.subSubCategories) {
        const activeSubSubcategory = subCategory?.subSubCategories?.[activeSubSubcategoryIndex]
        const currentActiveResourcesMetadata =
          resourcesMetadata?.find(
            (item: any) => item?.label?.toLowerCase() === activeSubSubcategory?.label?.toLowerCase()
          ) || {}
        return currentActiveResourcesMetadata
      } else {
        return currentResourcesMetadata
      }
    }, [resourceCategoriesMetadata, expandedSubcategory, activeSubSubcategoryIndex, resourcesMetadata])

    const handleSubcategoryOpen = useCallback((index: number, subcategoryItem: any) => {
      subcategoryItem?.subSubCategories?.length > 0 ? setActiveSubSubcategoryIndex(0) : setActiveSubSubcategoryIndex(-1)

      setExpandedSubcategory(prev => prev.map((item, i) => ({ ...item, open: i === index ? !item.open : false })))
    }, [])

    const handleSubSubcategoryOpen = (index: number, subSubindex: number) => {
      setExpandedSubcategory(prev =>
        prev.map(item => (item.index === index ? { ...item, open: true } : { ...item, open: false }))
      )
      setActiveSubSubcategoryIndex(subSubindex)
    }

    return (
      <Container childClassName="lg-desktop:gap-12 flex bg-white gap-8">
        <div
          className="lg-desktop:basis-[356px] flex basis-[284px] flex-col gap-4 py-4"
          style={{
            userSelect: 'none',
          }}
          ref={resourceDropdownRef}
        >
          <div
            className="desktop:h-[416px] flex flex-col overflow-y-auto"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {resourceCategoriesMetadata?.subcategories?.map((subItem: any, index: number) => {
              const hasSubSubCategory =
                Array.isArray(subItem?.subSubCategories) && subItem?.subSubCategories?.length > 0
              const isExpanded = expandedSubcategory?.find(item => item.index === index)?.open
              return (
                <div key={`subcategoryItem-${index}`}>
                  <button
                    className={cn(
                      'rounded-sidebar-shelf flex w-full cursor-pointer items-center justify-between border-0 bg-transparent text-left',
                      {
                        'bg-[#F5F5F7]': !hasSubSubCategory && isExpanded,
                      }
                    )}
                    onClick={() => {
                      handleSubcategoryOpen(index, subItem)
                      onSidebarNavClick?.(subItem, index)
                    }}
                    aria-expanded={hasSubSubCategory ? isExpanded : undefined}
                    aria-haspopup={hasSubSubCategory ? 'true' : undefined}
                  >
                    <Text html={subItem.label} className="p-4 text-sm font-bold leading-[1.4]" />
                    {hasSubSubCategory && (
                      <DownArrow
                        aria-hidden="true"
                        className={cn('size-4', {
                          ['rotate-180']: isExpanded,
                        })}
                      />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="flex flex-col" role="menu">
                      {subItem.subSubCategories?.map((subSubItem: any, subSubindex: number) => (
                        <button
                          onClick={() => {
                            handleSubSubcategoryOpen(index, subSubindex)
                            onSidebarNavClick?.(subSubItem, subSubindex)
                          }}
                          key={`subSubItem-${index}-${subSubindex}`}
                          className={cn(
                            'rounded-sidebar-shelf w-full cursor-pointer border-0 bg-transparent px-6 py-4 text-left leading-none hover:bg-[#F5F5F7]',
                            {
                              'bg-[#F5F5F7]': activeSubSubcategoryIndex === subSubindex,
                            }
                          )}
                          role="menuitem"
                          aria-label={subSubItem.label}
                        >
                          <Text html={subSubItem.label} className="text-sm font-bold leading-[1.4] text-[#4A4C56]" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {resourceCategoriesMetadata && (
            <div className="flex">
              <div className="flex flex-col gap-4">
                {resourceCategoriesMetadata?.primary && (
                  <Button
                    as="a"
                    href={`${getLocalizedPath(resourceCategoriesMetadata?.primary?.url, locale)}?ref=${resourceCategoriesMetadata?.primary?.label}_shopall`}
                    variant="primary"
                    size="lg"
                    className="lg-desktop:text-base text-sm"
                  >
                    {resourceCategoriesMetadata?.primary?.label}
                  </Button>
                )}
                {resourceCategoriesMetadata?.secondary && (
                  <Button
                    as="a"
                    href={getLocalizedPath(resourceCategoriesMetadata?.secondary?.url, locale)}
                    variant="link"
                    size="lg"
                    className="lg-desktop:text-base justify-start !p-0 text-sm"
                  >
                    {resourceCategoriesMetadata?.secondary?.label}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        <div
          className="desktop:h-[509px] flex-1 overflow-y-auto py-4"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <div className="grid grid-cols-3 gap-4 ">
            {Array.isArray(matchResourcesMetadata?.resources)
              ? matchResourcesMetadata.resources.map((resourceItem: any, resourceIndex: number) => (
                  <ResourceItem key={`resourceItem-${resourceIndex}`} resource={resourceItem} />
                ))
              : null}
          </div>
        </div>
      </Container>
    )
  }
)

ResourceSidebarDropdown.displayName = 'ResourceSidebarDropdown'
